import React, { useRef, useState } from "react";

import ErrorIcon from "@mui/icons-material/ErrorOutline";
import { Stack } from "@mui/material";

import { useTheme } from "@fartherfinance/frontend/theme/ThemeProvider";

import { TableContainerV2 } from "../../Client/Portfolio/shared/shared";
import Chip from "@src/sharedComponents/Chip/Chip";
import BorderedList from "@src/sharedComponents/List/BorderedList";
import Skeleton from "@src/sharedComponents/Skeleton/Skeleton";
import { CopyButton } from "@src/sharedComponents/Table/TableCell/TableCell";
import Text from "@src/sharedComponents/Typography/Text";

import stylesModelDetailsTable from "./ModelDetailsTable.module.css";

type ModelDetailsTableProps = {
  riskLabel: string;
  taxLabel: string;
  createTime: string;
  portfolioId: string;
  isLoading: boolean;
  hasError: boolean;
};

const ModelDetailsTable = ({
  createTime,
  hasError,
  isLoading,
  riskLabel,
  taxLabel,
  portfolioId,
}: ModelDetailsTableProps): JSX.Element => {
  const {
    color: { $textInProgress, $avatarSteelBlue, $borderBold },
  } = useTheme();

  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onCopyClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation();

    setShowTooltip(true);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => setShowTooltip(false), 800);

    navigator.clipboard.writeText(portfolioId);
  };

  if (isLoading) {
    return (
      <div
        style={{
          marginBottom: "25px",
          border: `1px solid ${$borderBold}`,
          borderRadius: "5px",
        }}
      >
        <BorderedList
          rows={[
            {
              left: "Risk Level",
              right: <Skeleton width={120} />,
            },
            {
              left: "Tax Type",
              right: <Skeleton width={120} />,
            },
            {
              left: "Created",
              right: <Skeleton width={120} />,
            },
            {
              left: "Portfolio ID",
              right: <Skeleton width={120} />,
            },
          ]}
        />
      </div>
    );
  }

  if (hasError) {
    return <ErrorState />;
  }

  return (
    <div className={stylesModelDetailsTable.container}>
      <BorderedList
        rows={[
          {
            left: "Risk Level",
            right: <Chip label={riskLabel} />,
          },
          {
            left: "Tax Type",
            right: <Chip label={taxLabel} />,
          },
          {
            left: "Created",
            right: (
              <div className={stylesModelDetailsTable.dateText}>
                {createTime}
              </div>
            ),
          },
          {
            left: "Portfolio ID",
            right: (
              <Stack
                style={{
                  cursor: "pointer",
                  flex: 1,
                }}
                direction={"row"}
                spacing={1}
                onClick={onCopyClick}
              >
                <CopyButton
                  tooltipText="Copied"
                  placement="top"
                  disableHoverListener
                  showTooltip={showTooltip}
                >
                  <div
                    style={{
                      color: $textInProgress,
                      backgroundColor: $avatarSteelBlue,
                      borderRadius: "4px",
                      padding: "2px 6px",
                    }}
                  >
                    {portfolioId}
                  </div>
                </CopyButton>
              </Stack>
            ),
          },
        ]}
      />
    </div>
  );
};

const ErrorState = () => {
  return (
    <TableContainerV2>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        style={{
          height: "200px",
          justifyContent: "center",
        }}
      >
        <ErrorIcon color={"info"} fontSize="small" />
        <Text variant="body" color="textSubtle">
          Error loading table
        </Text>
      </Stack>
    </TableContainerV2>
  );
};

export default ModelDetailsTable;
